import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCalendarDate } from '@shared/store/dashboard.duck';
import {
  loadTeamScopedWhereabouts,
  loadWhereabouts,
  loadWorkgroupScopedWhereabouts,
} from '@features/whereabouts/pages/team-movements/models/team-movements.duck';
import { scopeKey, WhereaboutsScope } from '@features/whereabouts/models/whereaboutsScope';
import { useFollowedGroupIds } from './useFollowedGroupIds';

// Keeps the `teamMovements` slice loaded with the week's whereabouts for the
// given scope, re-fetching when the calendar date or scope changes. For the
// followed scope this also re-fetches when the followed groups change, and
// skips the request when nothing is followed (matching the previous behaviour
// of the week views).
export function useScopedWeeklyWhereaboutsLoader(scope: WhereaboutsScope): void {
  const dispatch = useDispatch();
  const calendarDate = useSelector(selectCalendarDate);
  const { teamIds: followedTeamIds, workGroupIds: followedWorkGroupIds } = useFollowedGroupIds();
  const key = scopeKey(scope);

  useEffect(() => {
    if (!calendarDate) return;

    switch (scope.kind) {
      case 'team':
        dispatch(loadTeamScopedWhereabouts({ teamId: scope.teamId, calendarDate }));
        return;
      case 'workgroup':
        dispatch(loadWorkgroupScopedWhereabouts({ workGroupId: scope.workGroupId, calendarDate }));
        return;
      default:
        if (followedTeamIds.length === 0 && followedWorkGroupIds.length === 0) return;
        dispatch(loadWhereabouts({ calendarDate }));
    }
    // `key` stands in for `scope`, which callers may recreate each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, calendarDate, key, followedTeamIds, followedWorkGroupIds]);
}
